import React, { useState } from 'react';
import { ExternalLink, MapPin, Globe, Image, Film, Building2 } from 'lucide-react';
import { Exhibitor } from './types';
import { CategoryBadge } from './CategoryBadge';

interface ExpoCardProps {
  exhibitor: Exhibitor;
  onClick: () => void;
  viewMode: 'grid' | 'list';
  primaryColor?: string;
}

export const ExpoCard: React.FC<ExpoCardProps> = ({
  exhibitor,
  onClick,
  viewMode,
  primaryColor = "#2563eb" // Default to blue-600
}) => {
  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const getLogoUrl = () => {
    if (exhibitor.displaySize === 'large') return exhibitor.logo.largeUrl || exhibitor.logo.url;
    if (exhibitor.displaySize === 'small') return exhibitor.logo.smallUrl || exhibitor.logo.url;
    return exhibitor.logo.mediumUrl || exhibitor.logo.url;
  };

  const logoUrl = getLogoUrl();
  const hasLogo = exhibitor.logo && (exhibitor.logo.url || exhibitor.logo.smallUrl || exhibitor.logo.mediumUrl || exhibitor.logo.largeUrl);

  // Helper function to detect media presence in description
  const detectMedia = (html: string | undefined) => {
    if (!html) return { hasImage: false, hasVideo: false, hasEmbed: false };
    
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    const hasImage = temp.querySelectorAll('img, picture, svg').length > 0;
    const hasVideo = temp.querySelectorAll('video, iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="wistia"], iframe[src*="loom"]').length > 0;
    const hasEmbed = temp.querySelectorAll('iframe, embed, object').length > 0 && !hasVideo;
    
    return { hasImage, hasVideo, hasEmbed };
  };

  const mediaInfo = detectMedia(exhibitor.description);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Ensure backward compatibility
  const companyName = exhibitor.companyName || exhibitor.name;

  if (viewMode === 'list') {
    return (
      <button
        onClick={onClick}
        className="w-full p-4 border rounded-lg transition-all bg-white text-left group shadow-sm hover:shadow-md"
        style={{ 
          borderColor: '#e5e7eb',
          '--tw-border-opacity': 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = hexToRgba(primaryColor, 0.3);
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center">
            {hasLogo && !logoError ? (
              <img
                src={logoUrl}
                alt={companyName}
                className="w-full h-full object-contain p-2"
                onError={() => setLogoError(true)}
              />
            ) : (
              <Building2 size={32} className="text-gray-400" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{companyName}</h3>

            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
              {exhibitor.boothLocation && (
                <div className="flex items-center gap-1">
                  <MapPin size={14} />
                  <span>{exhibitor.boothLocation}</span>
                </div>
              )}
              {/* Show universal categories */}
              {exhibitor.universalCategories && exhibitor.universalCategories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {exhibitor.universalCategories.map(category => (
                    <CategoryBadge key={category.id} category={category} size="sm" />
                  ))}
                </div>
              )}
              {/* Fallback to legacy category if no universal categories */}
              {(!exhibitor.universalCategories || exhibitor.universalCategories.length === 0) && exhibitor.category && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                  {exhibitor.category}
                </span>
              )}
              {(mediaInfo.hasImage || mediaInfo.hasVideo || mediaInfo.hasEmbed) && (
                <div className="flex items-center gap-1">
                  {mediaInfo.hasImage && <Image size={14} style={{ color: primaryColor }} />}
                  {mediaInfo.hasVideo && <Film size={14} className="text-purple-600" />}
                  {mediaInfo.hasEmbed && <Globe size={14} className="text-green-600" />}
                </div>
              )}
              {exhibitor.ctaText && (
                <div 
                  className="flex items-center group-hover:opacity-80 transition-opacity"
                  style={{ color: primaryColor }}
                >
                  <span className="text-sm">{exhibitor.ctaText}</span>
                  <ExternalLink size={12} className="ml-1" />
                </div>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  }

  // Grid view
  return (
    <button
      onClick={onClick}
      className="group border rounded-lg transition-all bg-white text-left shadow-sm hover:shadow-md overflow-hidden"
      style={{ 
        borderColor: '#e5e7eb',
        '--tw-border-opacity': 1
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = hexToRgba(primaryColor, 0.3);
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div className="space-y-0">
        {/* Banner Image with blurred background */}
        <div className="aspect-[2/1] relative overflow-hidden bg-gray-50">
          {(exhibitor.banner.url || exhibitor.banner.mediumUrl || (hasLogo && !logoError)) && !bannerError ? (
            <div className="relative w-full h-full">
              {/* Blurred background image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={exhibitor.banner.url || exhibitor.banner.mediumUrl || logoUrl}
                  alt=""
                  className="w-full h-full object-cover blur-xl scale-110 opacity-30"
                  aria-hidden="true"
                />
                <div className="absolute inset-0 bg-white/20" />
              </div>
              
              {/* Main banner image */}
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={exhibitor.banner.url || exhibitor.banner.mediumUrl || logoUrl}
                  alt={`${companyName} banner`}
                  className="w-full h-full object-contain"
                  onError={() => setBannerError(true)}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Building2 size={48} className="text-gray-300" />
            </div>
          )}
        </div>

        {/* Content section with padding */}
        <div className="p-4 space-y-3">
          {/* Logo and Company Info */}
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 border transition-colors flex items-center justify-center">
              {hasLogo && !logoError ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="w-full h-full object-contain p-2"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Building2 size={24} className="text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{companyName}</h3>
              {/* Show universal categories */}
              {exhibitor.universalCategories && exhibitor.universalCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {exhibitor.universalCategories.map(category => (
                    <CategoryBadge key={category.id} category={category} size="sm" />
                  ))}
                </div>
              )}
              {/* Fallback to legacy category if no universal categories */}
              {(!exhibitor.universalCategories || exhibitor.universalCategories.length === 0) && exhibitor.category && (
                <span className="inline-block mt-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {exhibitor.category}
                </span>
              )}
            </div>
          </div>

          {/* Footer info */}
          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
            {exhibitor.boothLocation && (
              <div className="flex items-center gap-1">
                <MapPin size={14} />
                <span>{exhibitor.boothLocation}</span>
              </div>
            )}
            {(mediaInfo.hasImage || mediaInfo.hasVideo || mediaInfo.hasEmbed) && (
              <div className="flex items-center gap-1">
                {mediaInfo.hasImage && (
                  <span className="flex items-center gap-0.5" style={{ color: primaryColor }} title="Contains images">
                    <Image size={14} />
                  </span>
                )}
                {mediaInfo.hasVideo && (
                  <span className="flex items-center gap-0.5 text-purple-600" title="Contains video">
                    <Film size={14} />
                  </span>
                )}
                {mediaInfo.hasEmbed && (
                  <span className="flex items-center gap-0.5 text-green-600" title="Contains embedded content">
                    <Globe size={14} />
                  </span>
                )}
              </div>
            )}
            {exhibitor.ctaText && (
              <div 
                className="flex items-center group-hover:opacity-80 transition-opacity"
                style={{ color: primaryColor }}
              >
                <span className="text-sm">{exhibitor.ctaText}</span>
                <ExternalLink size={12} className="ml-1" />
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};